import {useCallback, useEffect, useState} from 'react';
import {Client, Frame, IMessage, StompConfig} from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET!;
const HEARTBEAT_MS = 20_000;

type Status = 'connecting' | 'connected' | 'disconnected' | 'error';

type JsonHandler<T> = (body: T) => void;

interface SubscriptionEntry {
    topic: string;
    handler: JsonHandler<unknown>;
    unsub?: () => void;
}

const listeners = new Set<(status: Status) => void>();
const subscriptions = new Map<number, SubscriptionEntry>();

let nextSubId = 1;
let sharedClient: Client | null = null;
let status: Status = 'disconnected';
let retryCount = 0;
let generation = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let isConnecting = false;
let isShuttingDown = false;

const clearReconnectTimer = () => {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
};

const setSharedStatus = (nextStatus: Status) => {
    status = nextStatus;
    listeners.forEach((listener) => listener(nextStatus));
};

const backoffDelay = () => {
    const base = Math.min(30_000, 1000 * 2 ** retryCount);
    const jitter = 0.5 + Math.random();
    retryCount = Math.min(retryCount + 1, 15);
    return Math.floor(base * jitter);
};

const unsubscribeAllActive = () => {
    subscriptions.forEach((entry) => {
        if (entry.unsub) {
            try { entry.unsub(); } catch {}
            entry.unsub = undefined;
        }
    });
};

const attachSubscription = (entry: SubscriptionEntry) => {
    if (!sharedClient?.connected || entry.unsub) return;

    const sub = sharedClient.subscribe(entry.topic, (msg: IMessage) => {
        try {
            entry.handler(JSON.parse(msg.body));
        } catch (e) {
            console.error('WS JSON parse error:', e, msg.body);
            entry.handler(msg.body as unknown);
        }
    });

    entry.unsub = () => {
        try { sub.unsubscribe(); } catch {}
    };
};

const reattachSubscriptions = () => {
    subscriptions.forEach((entry) => attachSubscription(entry));
};

const hardShutdown = async () => {
    clearReconnectTimer();
    unsubscribeAllActive();

    const client = sharedClient;
    sharedClient = null;

    if (client) {
        try {
            await client.deactivate();
        } catch {
        }
    }
};

const scheduleReconnect = () => {
    if (reconnectTimer || listeners.size === 0 || isShuttingDown) return;

    const delay = backoffDelay();
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connectShared();
    }, delay);
};

const handleDisconnect = (nextStatus: Extract<Status, 'disconnected' | 'error'>, myGeneration: number) => {
    if (generation !== myGeneration || isShuttingDown) return;
    isConnecting = false;
    unsubscribeAllActive();
    setSharedStatus(nextStatus);
    scheduleReconnect();
};

const connectShared = async () => {
    if (isShuttingDown || listeners.size === 0) return;
    if (isConnecting || sharedClient?.connected) return;

    isConnecting = true;
    setSharedStatus('connecting');

    await hardShutdown();

    if (isShuttingDown || listeners.size === 0) {
        isConnecting = false;
        setSharedStatus('disconnected');
        return;
    }

    const myGeneration = generation + 1;
    generation = myGeneration;

    const config: StompConfig = {
        webSocketFactory: () => new SockJS(SOCKET_URL),
        heartbeatOutgoing: HEARTBEAT_MS,
        heartbeatIncoming: HEARTBEAT_MS,
        connectionTimeout: 5_000,
        reconnectDelay: 0,
        debug: (msg) => console.debug('[STOMP]', msg),

        onConnect: (_frame: Frame) => {
            if (generation !== myGeneration || isShuttingDown) return;

            isConnecting = false;
            retryCount = 0;
            setSharedStatus('connected');
            reattachSubscriptions();
        },

        onStompError: () => {
            handleDisconnect('error', myGeneration);
        },
        onWebSocketClose: () => {
            handleDisconnect('disconnected', myGeneration);
        },
        onWebSocketError: () => {
            handleDisconnect('error', myGeneration);
        },
    };

    const client = new Client(config);
    sharedClient = client;

    try {
        client.activate();
    } catch {
        isConnecting = false;
        setSharedStatus('error');
        scheduleReconnect();
    }
};

const startShared = () => {
    isShuttingDown = false;
    void connectShared();
};

const stopSharedIfUnused = async () => {
    if (listeners.size > 0) return;
    isShuttingDown = true;
    isConnecting = false;
    retryCount = 0;
    await hardShutdown();
    setSharedStatus('disconnected');
};

export function useStomp() {
    const [localStatus, setLocalStatus] = useState<Status>(status);

    useEffect(() => {
        const listener = (nextStatus: Status) => {
            setLocalStatus(nextStatus);
        };

        listeners.add(listener);
        startShared();

        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && !sharedClient?.connected) {
                scheduleReconnect();
            }
        };
        const handleOnline = () => {
            if (!sharedClient?.connected) {
                scheduleReconnect();
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('online', handleOnline);

        return () => {
            listeners.delete(listener);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('online', handleOnline);
            void stopSharedIfUnused();
        };
    }, []);

    const subscribe = useCallback(
        <T,>(topic: string, handler: (body: T) => void) => {
            const id = nextSubId++;
            const entry: SubscriptionEntry = {
                topic,
                handler: handler as JsonHandler<unknown>,
            };

            subscriptions.set(id, entry);
            attachSubscription(entry);

            return () => {
                const current = subscriptions.get(id);
                if (!current) return;

                if (current.unsub) {
                    try { current.unsub(); } catch {}
                }

                subscriptions.delete(id);
            };
        },
        []
    );

    const sendMessage = useCallback((destination: string, body: any) => {
        const client = sharedClient;
        if (client?.connected) {
            try {
                client.publish({
                    destination,
                    body: JSON.stringify(body),
                    headers: { 'content-type': 'application/json' },
                });
            } catch (e) {
                console.error('STOMP publish failed', e);
            }
        } else {
            console.warn('STOMP not connected – message dropped', destination, body);
        }
    }, []);

    return { status: localStatus, subscribe, sendMessage };
}
